// =====================================================================================
// INVRT Radar — web console — consent banner designer — what is live right now
//
// The published state from GET /sites/:id/consent: version, when it went live, the loader
// snippet (with a copy button and the way to the Install tab), and the one warning that
// matters after a publish — the supplier's hosted banner still being on, which the server
// reports as `details.hosted_banner: 'disable_failed'` on the publish and as
// `hosted_banner_enabled: true` on every read after it.
// =====================================================================================

import { useState } from "react";
import { Link } from "react-router-dom";
import { Check, ClipboardCopy } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ui/toast";
import { formatDateTime } from "@/lib/format";
import type { ConsentSettings } from "@/lib/types.c1";

export interface PublishedSummaryProps {
  siteId: string;
  settings: ConsentSettings;
  /** True when the most recent publish in this session answered with hosted_banner: disable_failed. */
  hostedBannerDisableFailed: boolean;
  /** Advisory lines a publish response carried, shown verbatim. */
  warnings: string[];
}

export function PublishedSummary({
  siteId,
  settings,
  hostedBannerDisableFailed,
  warnings,
}: PublishedSummaryProps) {
  const { push } = useToast();
  const [copied, setCopied] = useState(false);
  const isPublished = settings.published_at !== null;
  const hostedStillOn = hostedBannerDisableFailed || (isPublished && settings.hosted_banner_enabled);

  async function copySnippet(): Promise<void> {
    try {
      await navigator.clipboard.writeText(settings.loader_snippet);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      push({
        tone: "warning",
        title: "Copying was blocked",
        body: "Your browser refused clipboard access. Select the snippet and copy it manually.",
      });
    }
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-2 text-sm text-ink-muted">
        {isPublished ? (
          <>
            <Badge tone="success">Live</Badge>
            <span>
              Version {settings.banner_version}, published {formatDateTime(settings.published_at)}.
            </span>
          </>
        ) : (
          <>
            <Badge tone="neutral">Not published yet</Badge>
            <span>
              Visitors see no Radar banner until you publish. Your install snippet stays as it
              is until then.
            </span>
          </>
        )}
        {settings.banner_is_default ? <span>The form shows Radar&apos;s default design.</span> : null}
      </div>

      {hostedStillOn || warnings.length > 0 ? (
        <div
          role="status"
          aria-label="Hosted banner warning"
          className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-warning"
        >
          {hostedStillOn ? (
            <p>
              Your banner is published, but the hosted banner could not be switched off, so
              visitors may see both for a while. Publish again to retry.
            </p>
          ) : null}
          {warnings.map((warning) => (
            <p key={warning}>{warning}</p>
          ))}
        </div>
      ) : null}

      {isPublished ? (
        <div className="flex flex-col gap-2">
          <div className="relative">
            <pre className="overflow-x-auto rounded-lg bg-ink px-4 py-3 text-xs text-white">
              <code>{settings.loader_snippet}</code>
            </pre>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              className="absolute right-2 top-2"
              onClick={() => void copySnippet()}
            >
              {copied ? (
                <Check className="h-3.5 w-3.5" aria-hidden="true" />
              ) : (
                <ClipboardCopy className="h-3.5 w-3.5" aria-hidden="true" />
              )}
              {copied ? "Copied" : "Copy snippet"}
            </Button>
          </div>
          <p className="text-xs text-ink-muted">
            This snippet replaces the pixel on your pages: it shows the banner and loads
            identification only after a visitor accepts.{" "}
            <Link
              to={`/sites/${siteId}?tab=install`}
              className="font-medium text-primary underline-offset-2 hover:underline"
            >
              Open the Install tab
            </Link>
          </p>
        </div>
      ) : null}
    </div>
  );
}
